import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useApi, useAsync } from '../../../hooks';
import { useParams } from 'react-router-dom';

const PolicyPageContext = createContext();

export const PolicyPageContextProvider = ({ children }) => {
  const { policyId, MCEId } = useParams();
  const {
    getPolicyWithId,
    getPolicyMonthlyReportingData,
    getPolicyMCEs,
    updatePolicyMonthlyReportingData,
    getEndorsements,
    getGroups,
    getSavedFormSchedule,
    getPolicyLegacyMCEs,
    getFormSchedule,
    getMonthlyStatements,
    downloadMonthlyStatementById,
  } = useApi();
  const [shouldShowWarning, setShouldShowWarning] = useState(true);
  const [locationsData, setLocationsData] = useState([]);
  const [coveragesData, setCoveragesData] = useState([]);
  const [showSplitModal, setShowSplitModal] = useState(false);
  const [currentTab, setCurrentTab] = useState('');
  const [editMonthlyReporting, setEditMonthlyReporting] = useState(false);
  const [fetchingPolicyDetails, setIsFetchingPolicyDetails] = useState(false);
  const [monthlyReportingData, setMonthlyReportingData] = useState([]);
  const [initialMonthlyReportingData, setInitialMonthlyReportingData] = useState([]);
  const [isUpdatingMonthly, setIsUpdatingMonthly] = useState(false);
  const [policyData, setPolicyData] = useState(null);
  const [MCEsData, setMCEsData] = useState(null);
  const [namedInsureds, setNamedInsureds] = useState([]);
  const [lossPayee, setLossPayee] = useState([]);
  const [endorsements, setEndorsements] = useState([]);
  const [highValueVehicles, setHighValueVehicles] = useState([]);
  const [temporaryLocations, setTemporaryLocations] = useState([]);
  const [temporaryFees, setTemporaryFees] = useState([]);
  const [savedFormSchedule, setSavedFormSchedule] = useState(null);
  const [groups, setGroups] = useState([]);
  const [combinedMode, setCombinedMode] = useState(true);
  const [isFetchingGroups, setIsFetchingGroups] = useState(false);
  const [showMCEColumns, setShowMCEColumns] = useState(false);
  const [monthlyStatements, setMonthlyStatements] = useState([]);
  const [executedMonthlyStatements, setExecutedMonthlyStatements] = useState([]);
  const [isFetchingMonthlyStatements, setIsFetchingMonthlyStatements] = useState(false);

  // Reset state when policyId changes (e.g. navigating from one policy to another after renewal)
  useEffect(() => {
    setPolicyData(null);
    setMCEsData(null);
    setEndorsements([]);
    setMonthlyReportingData([]);
    setInitialMonthlyReportingData([]);
  }, [policyId]);

  const fetchMonthlyStatements = useCallback(async () => {
    if (!policyId) return [];

    setIsFetchingMonthlyStatements(true);
    try {
      const statements = await getMonthlyStatements(policyId);

      setMonthlyStatements(statements || []);

      const executed = (statements || []).filter((stmt) => stmt.status?.toLowerCase() === 'executed');
      setExecutedMonthlyStatements(executed);

      return statements || [];
    } catch (error) {
      console.error('Failed to fetch monthly statements', error);
      setMonthlyStatements([]);
      setExecutedMonthlyStatements([]);
      return [];
    } finally {
      setIsFetchingMonthlyStatements(false);
    }
  }, [policyId, getMonthlyStatements]);

  const handleGetPolicyData = useCallback(async () => {
    if (policyData) {
      return policyData;
    }

    if (policyId && MCEId) {
      setIsFetchingPolicyDetails(true);
      try {
        const policy = await getPolicyWithId(policyId);
        const existingMCEs = await getPolicyMCEs(policyId);
        const legacyMCEs = await getPolicyLegacyMCEs(policyId);
        const MCEs = [...existingMCEs, ...legacyMCEs];
        setPolicyData({ policy });
        setMCEsData(MCEs);
        setTemporaryFees(policy?.fees);
        return { policy };
      } catch (error) {
        console.error('Error fetching policy data:', error);
      } finally {
        setIsFetchingPolicyDetails(false);
      }
      return;
    }

    if (!policyId) return;

    setIsFetchingPolicyDetails(true);
    try {
      const policy = await getPolicyWithId(policyId);
      const monthlyData = await getPolicyMonthlyReportingData(policyId);
      const existingMCEs = await getPolicyMCEs(policyId);
      const legacyMCEs = await getPolicyLegacyMCEs(policyId);
      const MCEs = [...existingMCEs, ...legacyMCEs];

      const endorsements = await getEndorsements(policyId);

      const fetchedData = { policy, monthlyData, MCEs };
      setPolicyData(fetchedData);
      setMCEsData(MCEs);
      setEndorsements(endorsements);
      setTemporaryFees(policy?.fees);
      return fetchedData;
    } catch (error) {
      console.error('Error fetching policy data:', error);
    } finally {
      setIsFetchingPolicyDetails(false);
    }
  }, [policyId, MCEId, policyData, getPolicyWithId, getPolicyMonthlyReportingData, getPolicyMCEs, getEndorsements, getPolicyLegacyMCEs]);

  const fetchAndSetGroups = useCallback(async () => {
    setIsFetchingGroups(true);
    try {
      const groups = await getGroups(policyId);
      setGroups(groups);
    } catch (error) {
      console.error('Error fetching groups data:', error);
    } finally {
      setIsFetchingGroups(false);
    }
  }, [policyId, getGroups]);

  const refreshMCEs = useCallback(async () => {
    if (!policyId) return;
    try {
      const existingMCEs = await getPolicyMCEs(policyId);
      const legacyMCEs = await getPolicyLegacyMCEs(policyId);
      const MCEs = [...existingMCEs, ...legacyMCEs];
      setMCEsData(MCEs);
    } catch (error) {
      console.error('Error refreshing MCEs:', error);
    }
  }, [policyId, getPolicyMCEs, getPolicyLegacyMCEs]);

  const handleUpdateMonthlyReportingData = useCallback(async () => {
    setIsUpdatingMonthly(true);
    try {
      const response = await updatePolicyMonthlyReportingData(policyId, monthlyReportingData);
      if (response) {
        setMonthlyReportingData(JSON.parse(JSON.stringify(response)));
        setInitialMonthlyReportingData(JSON.parse(JSON.stringify(response)));
      }
    } catch (error) {
      console.error('Failed to update monthly reporting data:', error);
    } finally {
      setEditMonthlyReporting(false);
      setIsUpdatingMonthly(false);
    }
  }, [policyId, monthlyReportingData, updatePolicyMonthlyReportingData]);

  const handleResetMonthlyReportingData = useCallback(() => {
    setMonthlyReportingData(JSON.parse(JSON.stringify(initialMonthlyReportingData)));
  }, [initialMonthlyReportingData]);

  const { value, status } = useAsync(handleGetPolicyData);

  useEffect(() => {
    fetchAndSetGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { policy, monthlyData } = value || {};

  useEffect(() => {
    if (monthlyData) {
      setMonthlyReportingData(JSON.parse(JSON.stringify(monthlyData)));
      setInitialMonthlyReportingData(JSON.parse(JSON.stringify(monthlyData)));
    }
  }, [monthlyData]);

  useEffect(() => {
    const fetchFormSchedule = async () => {
      try {
        const response = await getSavedFormSchedule(policyId);
        setSavedFormSchedule(response);
      } catch (err) {
        console.log('error:', err);
      }
    };
    if (policyId && !MCEId) {
      fetchFormSchedule();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [policyId]);

  useEffect(() => {
    if (policyId) {
      fetchMonthlyStatements();
    }
  }, [policyId, fetchMonthlyStatements]);

  const context = useMemo(
    () => ({
      shouldShowWarning,
      setShouldShowWarning,
      locationsData,
      setLocationsData,
      coveragesData,
      setCoveragesData,
      showSplitModal,
      setShowSplitModal,
      currentTab,
      setCurrentTab,
      editMonthlyReporting,
      setEditMonthlyReporting,
      policy,
      status,
      MCEsData,
      monthlyReportingData,
      setMonthlyReportingData,
      handleUpdateMonthlyReportingData,
      handleResetMonthlyReportingData,
      isUpdatingMonthly,
      initialMonthlyReportingData,
      fetchingPolicyDetails,
      setIsUpdatingMonthly,
      namedInsureds,
      setNamedInsureds,
      setMCEsData,
      endorsements,
      setEndorsements,
      highValueVehicles,
      setHighValueVehicles,
      temporaryLocations,
      setTemporaryLocations,
      temporaryFees,
      setTemporaryFees,
      lossPayee,
      setLossPayee,
      savedFormSchedule,
      setSavedFormSchedule,
      groups,
      setGroups,
      combinedMode,
      setCombinedMode,
      fetchAndSetGroups,
      isFetchingGroups,
      showMCEColumns,
      setShowMCEColumns,
      monthlyStatements,
      executedMonthlyStatements,
      fetchMonthlyStatements,
      isFetchingMonthlyStatements,
      downloadMonthlyStatementById,
      refreshMCEs,
    }),
    [
      policy,
      status,
      MCEsData,
      monthlyReportingData,
      editMonthlyReporting,
      isUpdatingMonthly,
      setEditMonthlyReporting,
      shouldShowWarning,
      showSplitModal,
      setShowSplitModal,
      currentTab,
      setCurrentTab,
      setMonthlyReportingData,
      handleResetMonthlyReportingData,
      setIsUpdatingMonthly,
      fetchingPolicyDetails,
      coveragesData,
      handleUpdateMonthlyReportingData,
      initialMonthlyReportingData,
      locationsData,
      namedInsureds,
      setNamedInsureds,
      setMCEsData,
      endorsements,
      setEndorsements,
      highValueVehicles,
      setHighValueVehicles,
      temporaryLocations,
      setTemporaryLocations,
      temporaryFees,
      setTemporaryFees,
      lossPayee,
      groups,
      setGroups,
      setLossPayee,
      setSavedFormSchedule,
      savedFormSchedule,
      combinedMode,
      setCombinedMode,
      fetchAndSetGroups,
      isFetchingGroups,
      showMCEColumns,
      setShowMCEColumns,
      monthlyStatements,
      executedMonthlyStatements,
      fetchMonthlyStatements,
      isFetchingMonthlyStatements,
      downloadMonthlyStatementById,
      refreshMCEs,
    ]
  );

  return <PolicyPageContext.Provider value={context}>{children}</PolicyPageContext.Provider>;
};

export const usePolicyPageContext = () => {
  const context = useContext(PolicyPageContext);
  if (!context) {
    throw new Error('usePolicyPageContext must be used within a PolicyPageContextProvider');
  }
  return context;
};
